'use memo';

import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { AlertCard } from '@/components/AlertCard';
import { FillScreen } from '@/components/Layout';
import { Loader } from '@/components/Loading/Loader';
import { Text } from '@/components/Typography/Text';
import { DuplicatePatternDialog } from '@/features/operation-package-config-new/DuplicatePatternDialog';
import { useGetOperationPackageConfigs } from '@/features/operation-package-config-new/PackagePatternConfig/-query';
import { usePackagePatternConfigActions } from '@/features/operation-package-config-new/PackagePatternConfig/-query/useConfigTableActions';
import { findPatternInConfigRows, isDraftOnlyPattern, shouldUseInHouseDraftEditor } from '@/features/operation-package-config-new/PackagePatternConfig/-utils/findPatternInConfigRows';
import { isInHouseItocPackageType } from '@/features/operation-package-config-new/PackagePatternConfig/-utils/packageTypePredicates';
import { PackagePatternConfigDetail } from '@/features/operation-package-config-new/PackagePatternDetail';
import { PackagePatternDraftConfig } from '@/features/operation-package-config-new/PackagePatternDraftConfig';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { PatternDetailHeaderActions } from './-components/PatternDetailHeaderActions';
import { useItOperationCoreEligibleTargetSlot } from './-hooks/useItOperationCoreEligibleTargetSlot';
export const Route = createFileRoute('/_staff/it-operation-core/config/$patternId/')({
  component: PatternConfigPage
});
function PatternConfigPage() {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.itOperationCore'
  });
  const {
    t: tWarning
  } = useTranslation(defaultNS, {
    keyPrefix: 'features.operationPackageConfig.editDisabledWarning'
  });
  const {
    t: tArchivedNotice
  } = useTranslation(defaultNS, {
    keyPrefix: 'features.operationPackageConfig.archivedReadOnlyNotice'
  });
  const {
    patternId
  } = useParams({
    from: '/_staff/it-operation-core/config/$patternId/'
  });
  const navigate = useNavigate();
  const actions = usePackagePatternConfigActions('IT_OPERATION_CORE');
  const {
    data: configRows,
    isFetching: isConfigsFetching
  } = useGetOperationPackageConfigs('IT_OPERATION_CORE');

  // 初回ロードのみ全画面 Loader を出す。initialData({ packages: [] }) により isPending は
  // 常に false なので、初回だけ isFetching で待つ。一度でも fetch が完了したら、タブ復帰や
  // 保存後の一覧 invalidate による「背景 refetch」では Loader に戻さない。ここで戻すと配下の
  // 下書きエディタ（未保存の設問入力を保持）が unmount され、入力内容が失われる（DC-8443）。
  // 解決済みフラグは render 中に条件付き setState で更新する（React 推奨の派生 state パターン）。
  const [hasResolvedConfigs, setHasResolvedConfigs] = useState(false);
  if (!hasResolvedConfigs && !isConfigsFetching) {
    setHasResolvedConfigs(true);
  }
  const isInitialConfigsLoading = !hasResolvedConfigs && isConfigsFetching;

  // 複製して編集: 名前入力ダイアログを開き、確定後に複製 API を name 付きで呼ぶ。
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);
  const patternContext = useMemo(() => findPatternInConfigRows(configRows ?? [], patternId), [configRows, patternId]);

  // 公開 / アーカイブ済み詳細のボタン・警告出し分け。In-House（テナント専用）のみ対象。
  // status / hasInstances は一覧行から取得。
  const pattern = patternContext?.pattern;
  const patternStatus = pattern?.status ?? (pattern?.isDraft ? 'draft' : 'published');
  const hasInstances = pattern?.hasInstances === true;
  const isInHousePattern = patternContext !== null && isInHouseItocPackageType(patternContext.packageType);
  // インスタンス無しの公開パターンの「変更を保存」は、下書きと同じ編集体験で公開版を
  // 直接更新する（draft GET → 編集 → PUT draft + publish）。そのため下書きエディタ経路に載せる。
  // それ以外（公開×インスタンス有り / アーカイブ済み）は複製して編集。
  const canEditInPlace = patternStatus === 'published' && !hasInstances;
  const inPlaceEditPublished = isInHousePattern && canEditInPlace;
  const showEditDisabledWarning = isInHousePattern && patternStatus === 'published' && hasInstances;

  // 読取専用で詳細（getConfig）を描画するケース:
  // - アーカイブ済み: 「完全に閲覧可能（読取専用）」が確定原則（どのプロダクトでも）。
  // - 公開×インスタンス有り（In-House）: 編集導線は「複製して編集」のみ（warningSlot と同義）。
  //   承認フロー編集 UI も封じる（BE も archived PUT を 409 で拒否する多重防御）。
  const readOnly = patternStatus === 'archived' || showEditDisabledWarning;
  const useInHouseDraftEditor = patternContext !== null && (shouldUseInHouseDraftEditor(patternContext) || inPlaceEditPublished);
  const isDraftOnly = patternContext !== null && isDraftOnlyPattern(patternContext);
  const backToList = () => {
    void navigate({
      to: '/it-operation-core/config'
    });
  };
  const duplicateAndEdit = () => {
    if (!pattern) return;
    setIsDuplicateDialogOpen(true);
  };
  // 公開×インスタンス有り / アーカイブ済み（In-House）: 複製して編集 / 一覧に戻る。
  // 公開×インスタンス無しはここに来ない（下書きエディタでその場編集する）。
  const detailActionSlot = isInHousePattern ? <PatternDetailHeaderActions onDuplicateAndEdit={duplicateAndEdit} onBackToList={backToList} isDuplicating={actions.duplicateMutation.isPending} /> : undefined;

  // 公開×インスタンス有り: 提出済み申請との整合を崩さないための編集不可警告。
  // アーカイブ済み: 読取専用である旨の案内（複製して編集へ誘導）。
  const warningSlot = showEditDisabledWarning ? <AlertCard variant="warning" title={tWarning('title')} description={tWarning('description')} /> : patternStatus === 'archived' ? <AlertCard variant="neutral" title={tArchivedNotice('title')} description={tArchivedNotice('description')} /> : undefined;
  const eligibleTargetSlot = useItOperationCoreEligibleTargetSlot(patternId, {
    // 一覧の初回ロード完了後にのみ有効化する。背景 refetch（isConfigsFetching）で
    // toggle しないよう、transient な isConfigsFetching ではなく初回ロード判定を使う。
    enabled: !isInitialConfigsLoading && patternContext !== null && !useInHouseDraftEditor && !isDraftOnly
  });

  // 初回ロード中、または「複製直後などで patternId が一覧にまだ載っていない再解決中」は
  // Loader を出し、getConfig（読取専用詳細）経路へ誤って落とさない。複製した新 Draft は
  // 一覧 refetch 完了後に patternContext として解決される。fetch 完了後も null のままなら
  // Loader を抜け、既存挙動（getConfig 詳細）に委ねる（存在しない patternId で無限ローダーに
  // しない）。既に解決済み（patternContext !== null）の背景 refetch では Loader に戻さない。
  if (isInitialConfigsLoading || patternContext === null && isConfigsFetching) {
    return <FillScreen className="flex items-center justify-center">
        <Loader />
      </FillScreen>;
  }
  if (useInHouseDraftEditor && patternContext) {
    return <PackagePatternDraftConfig ownerProduct="IT_OPERATION_CORE" packageId={patternContext.packageId} patternId={patternId} configListHref="/it-operation-core/config" configListLabel={t('title')} packageDisplayName={patternContext.packageName} inPlaceEditPublished={inPlaceEditPublished} />;
  }
  if (isDraftOnly) {
    return <FillScreen className="flex items-center justify-center px-12">
        <Text variant="p" className="text-neutral-muted-on-fill">
          {t('inHousePackages.selfReportPatternConfig.statusDraft')}
        </Text>
      </FillScreen>;
  }
  return <>
      <PackagePatternConfigDetail ownerProduct="IT_OPERATION_CORE" patternId={patternId} breadcrumbs={[{
      linkTo: '/it-operation-core/config',
      label: t('title')
    }]} eligibleTargetSlot={eligibleTargetSlot} actionSlot={detailActionSlot} warningSlot={warningSlot} readOnly={readOnly} />
      <DuplicatePatternDialog open={isDuplicateDialogOpen} onOpenChange={setIsDuplicateDialogOpen} sourceName={pattern?.name ?? ''} isLoading={actions.duplicateMutation.isPending} onSubmit={async ({
      name
    }) => {
      if (!pattern) return;
      const res = await actions.duplicateMutation.mutateAsync({
        row: pattern,
        name
      });
      if (!res.operationPackagePatternId) return;
      void navigate({
        to: '/it-operation-core/config/$patternId',
        params: {
          patternId: res.operationPackagePatternId
        }
      });
    }} />
    </>;
}